<template>
  <header class="site-header" :class="{ 'site-header--scrolled': isScrolled }">
    <div class="site-header__inner">
      <RouterLink to="/" class="brand" @click="closeMenu">
        <span class="brand__mark">K</span>

        <span class="brand__text">
          <span class="brand__name">krilin</span>
          <span class="brand__label">Photography | Videography | Webdesign</span>
        </span>
      </RouterLink>

      <nav class="desktop-nav" aria-label="Hauptnavigation">
        <a
          v-for="item in navItems"
          :key="item.id"
          href="#"
          class="desktop-nav__link"
          @click.prevent="scrollToSection(item.id)"
        >
          <span>{{ item.nr }}</span>
          {{ item.label }}
        </a>
      </nav>

      <a
        href="#"
        class="header-cta"
        @click.prevent="scrollToSection('contact')"
      >
        Anfrage starten
      </a>

      <button
        class="menu-button"
        type="button"
        :aria-expanded="menuOpen"
        aria-label="Navigation öffnen"
        @click="menuOpen = !menuOpen"
      >
        <span :class="{ active: menuOpen }"></span>
        <span :class="{ active: menuOpen }"></span>
      </button>
    </div>

    <transition name="mobile-menu">
      <nav v-if="menuOpen" class="mobile-nav" aria-label="Mobile Navigation">
        <a
          v-for="item in navItems"
          :key="item.id"
          href="#"
          class="mobile-nav__link"
          @click.prevent="handleMobileClick(item.id)"
        >
          <span>{{ item.nr }}</span>
          {{ item.label }}
        </a>

        <a
          href="#"
          class="mobile-nav__cta"
          @click.prevent="handleMobileClick('contact')"
        >
          Anfrage starten
        </a>
      </nav>
    </transition>
  </header>
</template>

<script setup>
import { nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const router = useRouter()
const route = useRoute()

const menuOpen = ref(false)
const isScrolled = ref(false)

const navItems = [
  { nr: '01', label: 'Services', id: 'services' },
  { nr: '02', label: 'Work', id: 'work' },
  { nr: '03', label: 'About', id: 'about' },
  { nr: '04', label: 'Kontakt', id: 'contact' },
]

function closeMenu() {
  menuOpen.value = false
}

function handleScroll() {
  isScrolled.value = window.scrollY > 16
}

async function scrollToSection(id) {
  closeMenu()

  if (route.path !== '/') {
    await router.push('/')
    await nextTick()
  }

  setTimeout(() => {
    const target = document.getElementById(id)

    if (!target) {
      return
    }

    const headerOffset = 110
    const targetPosition =
      target.getBoundingClientRect().top + window.pageYOffset - headerOffset

    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    })
  }, 80)
}

function handleMobileClick(id) {
  scrollToSection(id)
}

onMounted(() => {
  handleScroll()
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.site-header {
  --background: #2f2e2e;
  --secondary-background: #454545;
  --accent: #8b7054;
  --accent-secondary: #6c553d;
  --text-primary: #d6d1ce;
  --text-secondary: #e6e1de;

  position: fixed;
  inset: 0 0 auto;
  z-index: 100;
  border-bottom: 1px solid rgba(214, 209, 206, 0.08);
  background: rgba(47, 46, 46, 0.72);
  backdrop-filter: blur(18px);
  transition:
    background 220ms ease,
    border-color 220ms ease,
    box-shadow 220ms ease;
}

.site-header--scrolled {
  border-color: rgba(214, 209, 206, 0.14);
  background: rgba(47, 46, 46, 0.92);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.18);
}

.site-header__inner {
  width: min(100% - 40px, 1280px);
  min-height: 84px;
  margin-inline: auto;
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: center;
  gap: 28px;
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 14px;
  color: var(--text-secondary);
  text-decoration: none;
}

.brand__mark {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(214, 209, 206, 0.12);
  border-radius: 16px;
  background:
    radial-gradient(circle at 30% 20%, rgba(230, 225, 222, 0.18), transparent 35%),
    linear-gradient(135deg, var(--accent), var(--accent-secondary));
  color: var(--background);
  font-size: 1.2rem;
  font-weight: 900;
  letter-spacing: -0.08em;
  transition:
    transform 220ms ease,
    box-shadow 220ms ease;
}

.brand:hover .brand__mark {
  transform: translateY(-2px) rotate(-3deg);
  box-shadow: 0 14px 30px rgba(139, 112, 84, 0.22);
}

.brand__text {
  display: grid;
  gap: 2px;
}

.brand__name {
  color: var(--text-secondary);
  font-size: 1.2rem;
  font-weight: 900;
  letter-spacing: -0.055em;
  line-height: 1;
}

.brand__label {
  color: rgba(214, 209, 206, 0.58);
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.desktop-nav {
  justify-self: center;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(214, 209, 206, 0.08);
  border-radius: 999px;
  background: rgba(69, 69, 69, 0.34);
  padding: 6px;
}

.desktop-nav__link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  color: rgba(214, 209, 206, 0.78);
  font-size: 0.86rem;
  font-weight: 800;
  line-height: 1;
  padding: 12px 16px;
  text-decoration: none;
  transition:
    background 180ms ease,
    color 180ms ease,
    transform 180ms ease;
}

.desktop-nav__link span {
  color: var(--accent);
  font-size: 0.75rem;
}

.desktop-nav__link:hover {
  background: rgba(230, 225, 222, 0.08);
  color: var(--text-secondary);
  transform: translateY(-1px);
}

.header-cta,
.mobile-nav__cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  border-radius: 999px;
  background: var(--text-secondary);
  color: var(--background);
  font-size: 0.9rem;
  font-weight: 900;
  padding: 0 20px;
  text-decoration: none;
  transition:
    transform 180ms ease,
    background 180ms ease,
    color 180ms ease;
}

.header-cta:hover,
.mobile-nav__cta:hover {
  transform: translateY(-2px);
  background: var(--accent);
  color: var(--background);
}

.menu-button {
  display: none;
  width: 46px;
  height: 46px;
  border: 1px solid rgba(214, 209, 206, 0.12);
  border-radius: 16px;
  background: rgba(230, 225, 222, 0.06);
  cursor: pointer;
  position: relative;
}

.menu-button span {
  position: absolute;
  left: 12px;
  right: 12px;
  height: 2px;
  border-radius: 99px;
  background: var(--text-secondary);
  transition:
    transform 220ms ease,
    top 220ms ease;
}

.menu-button span:first-child {
  top: 17px;
}

.menu-button span:last-child {
  top: 27px;
}

.menu-button span:first-child.active {
  top: 22px;
  transform: rotate(45deg);
}

.menu-button span:last-child.active {
  top: 22px;
  transform: rotate(-45deg);
}

.mobile-nav {
  width: min(100% - 40px, 1280px);
  margin: 0 auto 18px;
  border: 1px solid rgba(214, 209, 206, 0.12);
  border-radius: 24px;
  background: rgba(69, 69, 69, 0.86);
  padding: 10px;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.28);
}

.mobile-nav__link {
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 16px;
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 800;
  padding: 16px;
  text-decoration: none;
}

.mobile-nav__link span {
  color: var(--accent);
  font-size: 0.82rem;
}

.mobile-nav__link:hover {
  background: rgba(230, 225, 222, 0.08);
  color: var(--text-secondary);
}

.mobile-nav__cta {
  width: 100%;
  margin-top: 8px;
}

.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition:
    opacity 220ms ease,
    transform 220ms ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 980px) {
  .site-header__inner {
    grid-template-columns: auto 1fr auto;
  }

  .desktop-nav,
  .header-cta {
    display: none;
  }

  .menu-button {
    display: inline-flex;
    justify-self: end;
  }
}

@media (max-width: 520px) {
  .site-header__inner {
    width: min(100% - 28px, 1280px);
    min-height: 76px;
  }

  .mobile-nav {
    width: min(100% - 28px, 1280px);
  }

  .brand__mark {
    width: 40px;
    height: 40px;
    border-radius: 14px;
  }

  .brand__label {
    display: none;
  }

  .brand__name {
    font-size: 1.08rem;
  }
}
</style>